import React, { createContext, useState, useContext, useCallback, useRef } from 'react';

const DocumentationContext = createContext();

export function DocumentationProvider({ children }) {
  // Current state
  const [activeSection, setActiveSection] = useState(null);
  const [activeSubsection, setActiveSubsection] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Reference to content area for scrolling
  const contentRef = useRef(null);
  
  // Navigation methods
  const navigateToSection = useCallback((sectionId) => {
    setActiveSection(sectionId);
    setActiveSubsection('');
    setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
    
    // Update URL without triggering a page reload
    window.history.replaceState(null, null, `#s=${sectionId}`);
  }, []);
  
  const navigateToSubsection = useCallback((sectionId, subsectionId) => {
    setActiveSection(sectionId);
    setActiveSubsection(subsectionId);
    setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
    
    // Update URL without triggering a page reload
    window.history.replaceState(null, null, `#s=${sectionId}&ss=${subsectionId}`);
  }, []);
  
  // Toggle section expansion without navigation
  const toggleSection = useCallback((sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  }, []);

  // Search handling
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
  }, []);

  const navigateToSearchResult = useCallback((result) => {
    if (result.type === 'section') {
      navigateToSection(result.id);
    } else {
      navigateToSubsection(result.sectionId, result.subsectionId);
    }
    clearSearch();
  }, [navigateToSection, navigateToSubsection, clearSearch]);
  
  const value = {
    activeSection,
    activeSubsection,
    expandedSections,
    searchQuery,
    searchResults,
    isSearching,
    contentRef,
    navigateToSection,
    navigateToSubsection,
    toggleSection,
    handleSearch,
    setSearchResults,
    navigateToSearchResult,
    clearSearch
  };
  
  return (
    <DocumentationContext.Provider value={value}>
      {children}
    </DocumentationContext.Provider>
  );
}

export function useDocumentation() {
  return useContext(DocumentationContext);
}